import type { TFunction } from 'i18next';

/** Mirrors `NoteVersionAuthor` from the API; see `NoteDiffPart` for why it is not imported. */
export type NoteVersionAuthorView =
  { kind: 'generated'; onBehalfOf: string | null } | { kind: 'person'; name: string } | { kind: 'unknown' };

/**
 * How a version's author reads to a clinician.
 *
 * Composed from both halves of the write's provenance, because both say something. A generated note
 * was the worker transforming *this practitioner's* transcript, so naming them is not incidental
 * detail — "Generated on behalf of Jay Marks" is a different fact from "Generated", and two notes
 * that both read "generated" differ when one was later edited by the clinician it was generated for
 * and the other was handed to someone else.
 *
 * `position` separates a creation from an edit, a distinction that is positional rather than
 * authorial: the first stored version of a hand-typed note is a creation, and calling it an edit
 * would be wrong — a note can have exactly one version a practitioner wrote directly, with no
 * worker involved at all.
 *
 * A record keyed on `kind` rather than a switch, so adding a kind to the API is a compile error
 * here instead of a silently blank line.
 */
export const noteVersionAuthorLine = (
  author: NoteVersionAuthorView,
  position: number,
  t: TFunction<'records'>,
): string => {
  const lines: Record<NoteVersionAuthorView['kind'], () => string> = {
    // A practitioner we cannot name falls back to a bare "Generated": true, and better than naming
    // a non-person or claiming the note was written on nobody's behalf.
    generated: () =>
      author.kind === 'generated' && author.onBehalfOf
        ? t('noteHistoryGeneratedFor', { name: author.onBehalfOf })
        : t('noteHistoryGenerated'),
    person: () =>
      author.kind === 'person'
        ? t(position === 1 ? 'noteHistoryCreatedBy' : 'noteHistoryEditedBy', { name: author.name })
        : t('noteHistoryUnknownAuthor'),
    unknown: () => t('noteHistoryUnknownAuthor'),
  };
  return lines[author.kind]();
};
